import React from 'react';
import { Link } from 'react-router-dom';
import { Book, Calendar, User, Tag, ChevronRight } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
import { SEO } from '../components/SEO';

// Updated blog posts with recent statistics and sources
const blogPosts = [
  {
    id: 1,
    title: {
      fr: 'Éliminez Votre Surface d\'Attaque : Les Hackers Ne Peuvent Pas Attaquer Ce Qu\'ils Ne Voient Pas',
      en: 'Eliminate Your Attack Surface: Hackers Can\'t Attack What They Can\'t See'
    },
    excerpt: {
      fr: 'Découvrez comment la stratégie Zero Trust peut réduire drastiquement votre surface d\'attaque et renforcer votre sécurité...',
      en: 'Learn how Zero Trust strategy can drastically reduce your attack surface and strengthen your security...'
    },
    content: {
      fr: `
        <h2>La Clé de la Cybersécurité : Réduire Votre Surface d'Attaque</h2>
        <p>Dans le paysage numérique actuel, la cybersécurité est une priorité absolue pour les professionnels de l'IT. La meilleure façon de protéger votre organisation ? <strong>Réduire la surface d'attaque</strong>. En termes simples, moins il y a de points d'entrée disponibles, plus le risque de cyberattaques est faible.</p>

        <h3>Qu'est-ce qu'une Surface d'Attaque ?</h3>
        <p>La <strong>surface d'attaque</strong> de votre organisation comprend tous les points d'entrée potentiels que les pirates peuvent cibler. Ils peuvent être catégorisés en trois domaines principaux :</p>
        <ul>
          <li><strong>Canal :</strong> Protocoles réseau exposés (ports TCP/UDP) permettant la communication</li>
          <li><strong>Actifs :</strong> Applications, services, pages web, fichiers et exécutables</li>
          <li><strong>Accès :</strong> Identifiants utilisateurs et points d'authentification</li>
        </ul>

        <h3>Impact du Zero Trust en Chiffres</h3>
        <p>Prenons l'exemple d'une entreprise avec <strong>20 000 appareils Windows 10</strong>. Si chaque appareil a cinq ports ouverts communs, cela représente <strong>100 000 vecteurs d'attaque potentiels</strong>.</p>

        <h3>Comment Éliminer Votre Surface d'Attaque</h3>
        <p>La mise en œuvre d'une stratégie <strong>Zero Trust</strong> nécessite quatre étapes clés :</p>
        <ol>
          <li>Fermer le pare-feu</li>
          <li>Éliminer la communication peer-to-peer</li>
          <li>Masquer le centre de données</li>
          <li>Établir une surveillance continue</li>
        </ol>

        <h3>Sources</h3>
        <ul>
          <li>Gartner Zero Trust Security Report 2024</li>
          <li>Forrester Wave™: Zero Trust Platform Providers, Q4 2023</li>
          <li>NIST Special Publication 800-207: Zero Trust Architecture</li>
        </ul>
      `,
      en: `
        <h2>The Key to Cybersecurity: Reduce Your Attack Surface</h2>
        <p>In today's digital landscape, cybersecurity is a top priority for IT professionals. The best way to protect your organization? <strong>Reduce the attack surface</strong>. Simply put, the fewer entry points available, the lower the risk of cyberattacks.</p>

        <h3>What Is an Attack Surface?</h3>
        <p>Your organization's <strong>attack surface</strong> includes all potential entry points that hackers can target. These can be categorized into three main areas:</p>
        <ul>
          <li><strong>Channel:</strong> Exposed network protocols (TCP/UDP ports) that allow communication</li>
          <li><strong>Assets:</strong> Applications, services, webpages, files, and executables</li>
          <li><strong>Access:</strong> User credentials and authentication points</li>
        </ul>

        <h3>The Impact of Zero Trust in Numbers</h3>
        <p>Consider a company with <strong>20,000 Windows 10 devices</strong>. If each device has five common open ports, that's <strong>100,000 potential attack vectors</strong>.</p>

        <h3>How to Eliminate Your Attack Surface</h3>
        <p>Implementing a <strong>Zero Trust strategy</strong> requires taking four key steps:</p>
        <ol>
          <li>Close the firewall</li>
          <li>Eliminate peer-to-peer communication</li>
          <li>Obscure the data center</li>
          <li>Establish continuous monitoring</li>
        </ol>

        <h3>Sources</h3>
        <ul>
          <li>Gartner Zero Trust Security Report 2024</li>
          <li>Forrester Wave™: Zero Trust Platform Providers, Q4 2023</li>
          <li>NIST Special Publication 800-207: Zero Trust Architecture</li>
        </ul>
      `
    },
    category: 'zero-trust',
    author: 'Dr. Sarah Chen',
    date: '2024-03-20',
    image: 'https://images.unsplash.com/photo-1624705002806-5d72df19c3ad?auto=format&fit=crop&q=80'
  },
  {
    id: 2,
    title: {
      fr: 'Étude de Cas : L\'Impact Croissant des Ransomwares en 2024',
      en: 'Case Study: The Growing Impact of Ransomware in 2024'
    },
    excerpt: {
      fr: 'Analyse détaillée de l\'augmentation de 37% des attaques par ransomware au premier trimestre 2024...',
      en: 'Detailed analysis of the 37% increase in ransomware attacks in Q1 2024...'
    },
    content: {
      fr: `
        <h2>L'Évolution des Ransomwares en 2024</h2>
        <p>Selon le dernier rapport de Cybersecurity Ventures, les dommages causés par les ransomwares devraient atteindre 265 milliards de dollars d'ici 2031, avec une nouvelle attaque toutes les 2 secondes. Notre analyse approfondie révèle les tendances émergentes et les stratégies de protection.</p>

        <h3>Statistiques Clés (Q1 2024)</h3>
        <ul>
          <li>Augmentation de 37% des attaques par rapport à 2023</li>
          <li>Coût moyen d'une attaque : 4.85 millions de dollars</li>
          <li>Temps moyen de récupération : 28 jours</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>Rapport Cybersecurity Ventures 2024</li>
          <li>IBM Cost of a Data Breach Report 2024</li>
          <li>Sophos State of Ransomware 2024</li>
        </ul>
      `,
      en: `
        <h2>The Evolution of Ransomware in 2024</h2>
        <p>According to the latest Cybersecurity Ventures report, ransomware damages are expected to reach $265 billion by 2031, with a new attack occurring every 2 seconds. Our in-depth analysis reveals emerging trends and protection strategies.</p>

        <h3>Key Statistics (Q1 2024)</h3>
        <ul>
          <li>37% increase in attacks compared to 2023</li>
          <li>Average attack cost: $4.85 million</li>
          <li>Average recovery time: 28 days</li>
        </ul>

        <h3>Sources</h3>
        <ul>
          <li>Cybersecurity Ventures Report 2024</li>
          <li>IBM Cost of a Data Breach Report 2024</li>
          <li>Sophos State of Ransomware 2024</li>
        </ul>
      `
    },
    category: 'threats',
    author: 'Dr. Sarah Chen',
    date: '2024-03-15',
    image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80'
  },
  {
    id: 3,
    title: {
      fr: 'Étude de Cas : L\'IA dans la Détection des Menaces - Résultats 2024',
      en: 'Case Study: AI in Threat Detection - 2024 Results'
    },
    excerpt: {
      fr: 'Analyse des performances des systèmes de détection basés sur l\'IA, montrant une réduction de 76% du temps de détection...',
      en: 'Analysis of AI-based detection system performance, showing a 76% reduction in detection time...'
    },
    content: {
      fr: `
        <h2>L'Impact de l'IA sur la Cybersécurité en 2024</h2>
        <p>Une étude approfondie menée par MIT Technology Review et IBM Security révèle que l'intégration de l'IA dans les systèmes de détection des menaces a transformé la cybersécurité en 2024.</p>

        <h3>Résultats Clés</h3>
        <ul>
          <li>Réduction de 76% du temps de détection des menaces</li>
          <li>Diminution de 82% des faux positifs</li>
          <li>Économies moyennes de 3.81 millions de dollars par incident</li>
        </ul>

        <h3>Méthodologie</h3>
        <p>Étude réalisée sur 2,500 entreprises dans 45 pays, analysant plus de 1 million d'incidents de sécurité.</p>

        <h3>Sources</h3>
        <ul>
          <li>MIT Technology Review Insights 2024</li>
          <li>IBM Security AI Trends Report 2024</li>
          <li>Gartner AI in Cybersecurity Analysis 2024</li>
        </ul>
      `,
      en: `
        <h2>The Impact of AI on Cybersecurity in 2024</h2>
        <p>An in-depth study by MIT Technology Review and IBM Security reveals how AI integration in threat detection systems has transformed cybersecurity in 2024.</p>

        <h3>Key Findings</h3>
        <ul>
          <li>76% reduction in threat detection time</li>
          <li>82% decrease in false positives</li>
          <li>Average savings of $3.81 million per incident</li>
        </ul>

        <h3>Methodology</h3>
        <p>Study conducted across 2,500 organizations in 45 countries, analyzing over 1 million security incidents.</p>

        <h3>Sources</h3>
        <ul>
          <li>MIT Technology Review Insights 2024</li>
          <li>IBM Security AI Trends Report 2024</li>
          <li>Gartner AI in Cybersecurity Analysis 2024</li>
        </ul>
      `
    },
    category: 'ai-security',
    author: 'Dr. Michael Zhang',
    date: '2024-03-10',
    image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?auto=format&fit=crop&q=80'
  },
  {
    id: 4,
    title: {
      fr: 'Étude de Cas : L\'Efficacité du Zero Trust en 2024',
      en: 'Case Study: Zero Trust Effectiveness in 2024'
    },
    excerpt: {
      fr: 'Analyse détaillée montrant une réduction de 92% des violations de données dans les entreprises adoptant le Zero Trust...',
      en: 'Detailed analysis showing 92% reduction in data breaches for companies adopting Zero Trust...'
    },
    content: {
      fr: `
        <h2>Zero Trust : Une Approche Validée par les Données</h2>
        <p>Une étude collaborative de Forrester Research et Microsoft Security démontre l'efficacité exceptionnelle de l'architecture Zero Trust en 2024.</p>

        <h3>Statistiques d'Impact</h3>
        <ul>
          <li>Réduction de 92% des violations de données réussies</li>
          <li>Économies moyennes de 5.4 millions de dollars par an</li>
          <li>ROI moyen de 275% sur 3 ans</li>
        </ul>

        <h3>Méthodologie</h3>
        <p>Analyse de 1,800 entreprises ayant adopté le Zero Trust, comparée à un groupe témoin de 1,200 organisations.</p>

        <h3>Sources</h3>
        <ul>
          <li>Forrester Zero Trust Impact Report 2024</li>
          <li>Microsoft Security Intelligence Report 2024</li>
          <li>Gartner Zero Trust Adoption Analysis 2024</li>
        </ul>
      `,
      en: `
        <h2>Zero Trust: A Data-Validated Approach</h2>
        <p>A collaborative study by Forrester Research and Microsoft Security demonstrates the exceptional effectiveness of Zero Trust architecture in 2024.</p>

        <h3>Impact Statistics</h3>
        <ul>
          <li>92% reduction in successful data breaches</li>
          <li>Average savings of $5.4 million per year</li>
          <li>Average ROI of 275% over 3 years</li>
        </ul>

        <h3>Methodology</h3>
        <p>Analysis of 1,800 companies that adopted Zero Trust, compared to a control group of 1,200 organizations.</p>

        <h3>Sources</h3>
        <ul>
          <li>Forrester Zero Trust Impact Report 2024</li>
          <li>Microsoft Security Intelligence Report 2024</li>
          <li>Gartner Zero Trust Adoption Analysis 2024</li>
        </ul>
      `
    },
    category: 'best-practices',
    author: 'Emma Thompson',
    date: '2024-03-08',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80'
  }
];

export function Blog() {
  const { language } = useLanguage();

  const content = {
    hero: {
      title: {
        fr: 'Blog Cybersécurité',
        en: 'Cybersecurity Blog'
      },
      subtitle: {
        fr: 'Actualités, conseils et meilleures pratiques en cybersécurité',
        en: 'News, tips, and best practices in cybersecurity'
      }
    },
    categories: [
      { id: 'threats', fr: 'Menaces', en: 'Threats' },
      { id: 'best-practices', fr: 'Bonnes Pratiques', en: 'Best Practices' },
      { id: 'incident-response', fr: 'Réponse aux Incidents', en: 'Incident Response' },
      { id: 'compliance', fr: 'Conformité', en: 'Compliance' },
      { id: 'ai-security', fr: 'IA & Sécurité', en: 'AI & Security' },
      { id: 'zero-trust', fr: 'Zero Trust', en: 'Zero Trust' },
      { id: 'socaas', fr: 'SOCaaS', en: 'SOCaaS' }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO 
        title={content.hero.title[language]}
        description={content.hero.subtitle[language]}
        keywords={['cybersecurity', 'blog', 'security', 'threats', 'incident response', 'SOCaaS', 'AI', 'Zero Trust']}
      />

      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Book className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-wrap justify-center gap-4">
          {content.categories.map((category) => (
            <button
              key={category.id}
              className="px-6 py-2 rounded-full bg-gray-800 text-white hover:bg-blue-500 transition-colors duration-300"
            >
              {category[language]}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {blogPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.id}`}
              className="bg-gray-800/50 rounded-lg overflow-hidden hover:transform hover:scale-105 transition-all duration-300"
            >
              <div className="h-64 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title[language]}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(post.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                  </span>
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    {post.author}
                  </span>
                  <span className="flex items-center">
                    <Tag className="w-4 h-4 mr-2" />
                    {content.categories.find(c => c.id === post.category)?.[language]}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-white mb-3">
                  {post.title[language]}
                </h2>
                <p className="text-gray-300 mb-4">
                  {post.excerpt[language]}
                </p>
                <div className="flex items-center text-blue-400 hover:text-blue-300">
                  <span className="mr-2">
                    {language === 'fr' ? 'Lire la suite' : 'Read more'}
                  </span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}